import React, { useEffect, useLayoutEffect, useState } from "react";
import { useParams } from "react-router";
import {
  IonButton,
  IonCard,
  IonCol,
  IonGrid,
  IonItem,
  IonRow,
  IonSpinner,
} from "@ionic/react";
import { useRegions } from "../data/store/useRegions";
import { useSongs } from "../data/store/useSongs";
import { useArtists } from "../data/store/useArtists";

import Content from "./Content";
import { usePageTitle } from "../util/sugar";
import isEmpty from "lodash.isempty";
import CompactSongList from "./CompactSongList";

export const RegionSongs: React.FC = () => {
  const { regionId } = useParams<{id:string, regionId:string}>();
  const { regions } = useRegions();
  const { status, songsTotal, songs, fetchSongsForRegion, regionSongs } = useSongs();
  const { regionArtists, fetchRegion } = useArtists();
  const region = regions[regionId];
  const songsForRegion = regionSongs[regionId];
  const artistsForRegion = regionArtists[regionId] || [];
  const [hasFetched, setHasFetched] = useState(false);

  usePageTitle(region ? region.city : "Loading");

  // Reset hasFetched when the region changes
  useEffect(() => {
    setHasFetched(false);
    fetchRegion(regionId);
  }, [regionId]);

  // Mark as fetched once songs come back from Firestore
  useEffect(() => {
    if (status === 'idle' && songsForRegion !== undefined) {
      setHasFetched(true);
    }
  }, [status, songsForRegion]);

  // Fetch songs once we have author IDs for this region
  useEffect(() => {
    if (artistsForRegion.length > 0 && status !== "fetching") {
      fetchSongsForRegion(regionId, artistsForRegion);
    }
    // eslint-disable-next-line
  }, [regionId, artistsForRegion.length]);

  const refreshPage = () => {
    window.location.reload();
  };

  useLayoutEffect(() => {
    if (!regionId) {
      refreshPage();
    }
  }, [regionId]);

  const noMoreSongs = (Object.keys(songs) || []).length === songsTotal;

  return (
    <Content classname="chill">
        <IonButton  expand="full" fill="clear" routerLink={"/region-artists/"+regionId}>View Artists</IonButton>
      <IonGrid>
        <IonRow>
          {!hasFetched && (
            <IonCol sizeMd="8" sizeLg="6" offsetMd="2" offsetLg="3">
              <IonRow className="ion-justify-content-center">
                <IonSpinner name="dots" />
              </IonRow>
            </IonCol>
          )}

          {hasFetched && isEmpty(songsForRegion) && (
            <IonCol sizeMd="8" sizeLg="6" offsetMd="2" offsetLg="3">
              <IonCard>
                <IonItem>
                  No songs found for this region ~
                  <IonButton onClick={refreshPage}>Refresh</IonButton>
                </IonItem>
              </IonCard>
            </IonCol>
          )}
        </IonRow>

        <IonRow>
          {!isEmpty(songsForRegion) && <IonCol sizeMd="8" sizeLg="6" offsetMd="2" offsetLg="3">
            {<CompactSongList
              status={status}
              songRefs={songsForRegion}
              fetchMore={() => fetchSongsForRegion(regionId, artistsForRegion)}
              loadMore={async () => {
                const before = new Set(
                  (useSongs.getState().regionSongs[regionId] || []).map((r) => r.id)
                );
                await useSongs.getState().fetchSongsForRegion(regionId, artistsForRegion);
                return (useSongs.getState().regionSongs[regionId] || [])
                  .map((r) => r.id)
                  .filter((id) => !before.has(id));
              }}
              hideFetchButton={noMoreSongs}/>}
          </IonCol>}
        </IonRow>
      </IonGrid>
    </Content>
  );
};
